import { LoggerProvider, SimpleLogRecordProcessor } from "@opentelemetry/sdk-logs";
import { ATTR_SERVICE_NAME, ATTR_SERVICE_VERSION } from "@opentelemetry/semantic-conventions";
import { resourceFromAttributes } from "@opentelemetry/resources";
import { OTLPLogExporter } from "@opentelemetry/exporter-logs-otlp-http";
import { SeverityNumber } from "@opentelemetry/api-logs";
import pageSpanStore from "$lib/stores/pageSpanStore";
import { get } from "svelte/store";
import pino from "pino";
import { getStandardAttributes } from "./opentelemetry";
import {
  spanFromCtxOrFallback,
  otelTraceIdToDdDecimal,
  otelSpanIdToDdDecimal
} from "./otelHelpers";

// don't use config here, it runs before the app is initialized
const accessor = import.meta.env ?? process.env;
const exportLevel = accessor.VITE_OTEL_LOG_LEVEL || "debug";
const consoleLevel = accessor.VITE_PINO_LOG_LEVEL || "debug";
const ddServiceName = accessor.VITE_TELEMETRY_SERVICE_NAME || "opc-web-utilities";
const envName = accessor.VITE_OTEL_DEPLOYMENT_ENVIRONMENT ?? "local";
const telemetryEnabled = accessor.VITE_TELEMETRY_ENABLED !== "false";
const otlpEndpoint = accessor.VITE_OTEL_EXPORTER_OTLP_ENDPOINT
  ? `${accessor.VITE_OTEL_EXPORTER_OTLP_ENDPOINT}/v1/logs`
  : null;

export const resource = resourceFromAttributes({
  [ATTR_SERVICE_NAME]: ddServiceName,
  [ATTR_SERVICE_VERSION]: "1.0.0",
  "dd.service": ddServiceName,
  ddsource: "webjs",
  env: envName,
  "deployment.environment": envName
});

const collectorOptions = {
  url: otlpEndpoint,
  headers: {
    mode: "cors",
    "Referrer-Policy": "origin"
  },
  concurrencyLimit: 10 // an optional limit on pending requests
};

const exporter = new OTLPLogExporter(collectorOptions);
const processors = [new SimpleLogRecordProcessor(exporter)];

// To start a logger, you first need to initialize the Logger provider.
const loggerProvider = new LoggerProvider({
  resource,
  processors
});

//  To create a log record, you first need to get a Logger instance
export const otel_logger = loggerProvider.getLogger(ddServiceName);

function loggerAttributes({ ctx } = {}) {
  const span = spanFromCtxOrFallback(ctx);
  const sc = span?.spanContext();
  const page = get(pageSpanStore); // { path, viewKey }
  const traceIdHex = sc?.traceId;
  const spanIdHex = sc?.spanId;

  const ddTraceId = traceIdHex ? otelTraceIdToDdDecimal(traceIdHex) : undefined;
  const ddSpanId = spanIdHex ? otelSpanIdToDdDecimal(spanIdHex) : undefined;

  const base = getStandardAttributes({
    path: page?.path,
    viewKey: page?.viewKey
  });

  return {
    ...base,
    service: ddServiceName,
    "dd.service": ddServiceName,
    env: envName,
    source: "webjs",
    ddsource: "webjs",
    "deployment.environment": envName,
    "otel.trace_id": traceIdHex,
    "otel.span_id": spanIdHex,
    "dd.trace_id": ddTraceId,
    "dd.span_id": ddSpanId
  };
}

const sendToOtelCollector = (logLevel, message, { ctx } = {}) => {
  if (telemetryEnabled) {
    otel_logger.emit({
      severityNumber: logLevel,
      body: message,
      attributes: loggerAttributes({ ctx })
    });
  } else {
    console.log("Not logging due to telemetry disabled:", message);
  }
};

//This method is used by the unit tests but is not used by the browser
//console.log is included for test debugging
const transmitter = {
  write: (data) => {
    const payload = JSON.parse(data);
    console.log(`${new Date(payload.time).toISOString()}:`, payload.msg);
    sendToOtelCollector(SeverityNumber[payload.level], payload.msg);
  }
};

const formatArgument = (arg) => {
  if (arg instanceof Error) {
    return arg.stack;
  } else if (arg instanceof Object) {
    return JSON.stringify(arg, null, 2);
  } else {
    return arg;
  }
};

const logger = pino(
  {
    level: consoleLevel,

    //Formatters and hooks are used only by server side elements.  These are not executed by
    //any JS that logs within the browser.
    formatters: {
      //Pino log levels do not match otel level, which makes server side logs look like errors
      level: (label) => {
        return { level: label };
      }
    },
    hooks: {
      //This resolves the issue with server side logs only logging the first statement log message
      logMethod(args, method) {
        let msg = formatArgument(args[0]);
        args.slice(1).forEach((arg) => {
          msg += ` ${formatArgument(arg)}`;
        });

        return method.apply(this, [msg]);
      }
    },

    //This section takes care of the browser based logs
    browser: {
      asObject: false,
      transmit: {
        level: exportLevel,
        send: (logLevelText, body) => {
          sendToOtelCollector(
            SeverityNumber[logLevelText.toUpperCase()],
            body.messages.map(formatArgument).join(" ")
          );
        }
      }
    }
  },
  transmitter
);

export default logger;
